
import { Fragment, useEffect } from 'react';
import SwiftCodeFinder from './SwiftCodeFinder';
import { trackPageView } from '../../utlis/useAnalytics';
import { PageName } from '../../configs/config';
import { Helmet } from "react-helmet-async";

const SwiftCodeFinderPage = () => {
  useEffect(() => {
    trackPageView(PageName.SwiftCodeFinder);
  }, []);

  return (
    <Fragment>
      {/* Original Swift Code Finder Tool Component */}
      <SwiftCodeFinder />
      <Helmet>
        <title>SWIFT Code Finder – Search Bank SWIFT/BIC Codes | EximPe</title>
        <meta name="description" content="Need a SWIFT code for international transfers? Use EximPe’s SWIFT Code Finder to locate SWIFT/BIC codes for any bank worldwide. Ensure secure & accurate transfers." />
        </Helmet>

      {/* SEO-Rich Content Section */}
      <section className="bg-white py-10 sm:py-14 md:py-16 mt-[-4%]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            {/* SEO Content Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 prose max-w-none text-gray-700">
              <h2 className="text-center lg:text-left md:text-left text-2xl sm:text-3xl font-bold text-gray-800 mb-6">
                BIC/SWIFT Code: What You Need to Know
              </h2>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">What is a BIC/SWIFT Code?</h3>
              <p>
                A BIC (or SWIFT code) is a special 8- or 11-digit code used to identify banks from different countries.
                With it, international payments are processed securely and correctly.
              </p>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">When Do You Need a BIC/SWIFT Code?</h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>A money transfer can only be sent or received abroad with an International Banking Number.</li>
                <li>Everybody involved in sending money between countries needs to be aware of FX rates.</li>
                <li>Banks commonly use it to determine which branch is supposed to handle the funds.</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">BIC/SWIFT Code Format</h3>
              <p>In this code, everything is set up as shown below:</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li><strong>Bank code (4 letters):</strong> Identifies the bank (state bank) in the first two to four letters</li>
                <li><strong>Country Code (2 letters):</strong> Shows the name of the country (such as IN for India)</li>
                <li><strong>Location Code (2 letters or digits):</strong> This shows the bank's city (for example, BB for Mumbai)</li>
                <li><strong>Optional Branch Code (3 letters or digits):</strong> To identify a particular branch, it displays 101 (for example).</li>
              </ul>
              <p>
                Without the branch code, it is 8 characters long; however, if included, it has 11 characters.
              </p>

              <h4 className="text-lg font-bold text-gray-800 mt-6 mb-3">Example:</h4>
              <p className="font-mono bg-gray-50 p-3 rounded-lg">
                SBININBB101<br />
                SBIN = State Bank of India<br />
                IN = India<br />
                BB = Mumbai<br />
                101 = Specific branch
              </p>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">Why is the BIC/SWIFT Code Important?</h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>It ensures that the receipt of international money ends up at the expected bank and branch.</li>
                <li>Sets up systems that prevent problems and slow down payments made from one country to another.</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">Details Needed for a SWIFT Transfer</h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>The person's name, where they live and the number of their bank account</li>
                <li>The recipient bank includes its name, address and a SWIFT or BIC reference.</li>
                <li>Transfer the amount, choose your currency and enter the type of payment.</li>
                <li>Information about the sender and recipient should be provided.</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">How to Find a SWIFT/BIC Code</h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>Review your bank balance on your statement or use the online banking service.</li>
                <li>Get in touch with your bank and ask them.</li>
                <li>Refer to lists provided by banks or SWIFT in your search.</li>
              </ul>

              <p className="font-bold mt-8">
                For seamless international payments with EximPe, always use the correct BIC/SWIFT code to ensure your
                funds reach the right destination quickly and securely.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default SwiftCodeFinderPage;
